import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { 
  Home, 
  Calendar, 
  BookOpen, 
  Users, 
  BarChart3,
  CreditCard,
  MessageCircle,
  MessageSquare,
  FileText,
  UserCircle
} from 'lucide-react';

const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const { isCollapsed } = useSidebar();
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Check if student is eligible for consultation
  const isStudentEligibleForConsultation = user?.role === 'STUDENT' &&
    user?.studentProfile?.consultationEnabled;

  const studentMenuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/schedule', label: 'Book Lessons', icon: Calendar },
    { path: '/quizzes', label: 'Quizzes', icon: FileText },
    { path: '/resources', label: 'Resources', icon: BookOpen },
    { path: '/feedback', label: 'Feedback', icon: MessageSquare },
    { path: '/payments', label: 'Payments', icon: CreditCard },
    { path: '/chat', label: 'Message Ethan', icon: MessageCircle },
  ];

  // Add consultation service if eligible
  if (isStudentEligibleForConsultation) {
    studentMenuItems.splice(1, 0, { path: '/consultation', label: 'Consultation Service', icon: MessageCircle });
  }

  const tutorMenuItems = [
    // Dashboard removed — redundant with the admin panel landing page.
    { path: '/admin', label: 'Admin Panel', icon: BarChart3 },
    { path: '/schedule', label: 'Schedule', icon: Calendar },
    { path: '/students', label: 'Students', icon: Users },
    { path: '/resources', label: 'Resources', icon: BookOpen },
    { path: '/quizzes', label: 'Quiz Management', icon: FileText },
  ];

  const menuItems = user?.role === 'TUTOR' ? tutorMenuItems : studentMenuItems;

  // Hide sidebar completely on mobile when collapsed
  if (isMobile && isCollapsed) {
    return null;
  }

  return (
    <aside className={`fixed left-0 top-16 h-full bg-white dark:bg-gray-800 shadow-sm border-r border-gray-200 dark:border-gray-700 z-20 transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      <nav className="mt-8">
        <div className="px-2">
          <div className="space-y-2">
            {Array.isArray(menuItems) ? menuItems.map((item) => {
              const Icon = item.icon;
              // Handle external links
              if ((item as any).external) {
                return (
                  <a
                    key={item.path}
                    href={(item as any).url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors group relative ${
                      isCollapsed ? 'justify-center' : 'space-x-3'
                    } text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span>{item.label}</span>}
                    
                    {/* Tooltip for collapsed state */}
                    {isCollapsed && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                        {item.label}
                      </div>
                    )}
                  </a>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors group relative ${
                    isCollapsed ? 'justify-center' : 'space-x-3'
                  } ${
                    isActive(item.path)
                      ? 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-r-2 border-primary-600'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.label}</span>}
                  
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </Link>
              );
            }) : null}
          </div>
        </div>

        <div className="mt-8 px-2">
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="space-y-2">
              <Link
                to="/profile"
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors group relative ${
                  isCollapsed ? 'justify-center' : 'space-x-3'
                }`}
                title={isCollapsed ? 'Profile' : ''}
              >
                <UserCircle className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span>Profile</span>}
                
                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    Profile
                  </div>
                )}
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar; 